import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CardActions,
  CircularProgress,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import RichTextDisplay from "@/components/common/RichTextDisplay";
import { Delete, Edit, Add } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RichTextEditor from "@/components/common/RichTextEditor";
import { EXERCISE_PART_TYPES } from "@/constants/exerciseConstants";
import { toast } from "react-toastify";
import {
  useAddExerciseDayMutation,
  useUpdateExerciseDayMutation,
  useRemoveExerciseDayMutation,
} from "@/store/api/admin/exerciseApi";
import { useRichTextWithUpload, useLazyLoad } from "@/hooks";
import { ExportPdfButton } from "@/components/common";
import { downloadExerciseDayPdf } from "@/utils/pdfExport";

const DayList = ({
  exercise,
  handleOpenQuestionManagement,
}) => {
  // Local state for day management
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [dayFormData, setDayFormData] = useState({
    title: "",
    dayNumber: 1,
  });
  const [isSubmittingDay, setIsSubmittingDay] = useState(false);

  // Rich text with upload hook
  const {
    description,
    descriptionState,
    hasImages,
    handleDescriptionChange,
    handleDescriptionStateChange,
    handleContentImgChange,
    processAndUploadImages,
    resetRichText,
    setRichTextValues,
  } = useRichTextWithUpload();

  // API hooks
  const [addExerciseDay, { isLoading: isAddingDay }] = useAddExerciseDayMutation();
  const [updateExerciseDay, { isLoading: isUpdatingDay }] = useUpdateExerciseDayMutation();
  const [removeExerciseDay] = useRemoveExerciseDayMutation();

  // Lazy loading for days
  const {
    displayedItems: displayedDays,
    allItems: sortedDays,
    isLoadingMore,
    hasMore: hasMoreDays,
    loadMore: loadMoreDays,
    loaderRef,
    remainingCount,
    itemsPerLoad,
  } = useLazyLoad(exercise?.days || [], {
    initialLoad: 5,
    itemsPerLoad: 5,
    sortFn: (a, b) => a.dayNumber - b.dayNumber,
    resetDependency: exercise?._id,
  });

  const getPartTypeChips = (day) => {
    const parts = [];
    Object.entries(EXERCISE_PART_TYPES).forEach(([key, config]) => {
      if (key === "description" || day[key]) {
        parts.push(
          <Chip
            key={key}
            label={config.name}
            size="small"
            color={config.color}
            variant={config.required ? "filled" : "outlined"}
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        );
      }
    });
    return parts;
  };

  const handleOpenDayDialog = (day = null) => {
    if (day) {
      setEditingDay(day);
      setDayFormData({
        title: day.title,
        dayNumber: day.dayNumber,
      });
      setRichTextValues(day.description || "");
    } else {
      setEditingDay(null);
      const nextDayNumber = exercise?.days?.length
        ? Math.max(...exercise.days.map((d) => d.dayNumber)) + 1
        : 1;
      setDayFormData({
        title: `Day ${nextDayNumber}`,
        dayNumber: nextDayNumber,
      });
      resetRichText();
    }
    setDayDialogOpen(true);
  };

  const handleCloseDayDialog = () => {
    setDayDialogOpen(false);
    setEditingDay(null);
    setDayFormData({
      title: "",
      dayNumber: 1,
    });
    resetRichText();
    setIsSubmittingDay(false);
  };

  const handleDayFormChange = (field, value) => {
    setDayFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitDay = async () => {
    if (!dayFormData.title.trim()) {
      toast.error("Day title is required");
      return;
    }

    if (!description.trim()) {
      toast.error("Description is required for all days");
      return;
    }

    setIsSubmittingDay(true);

    try {
      // Process and upload images, get final HTML content
      const htmlContent = await processAndUploadImages();

      // Submit the day with the final HTML content
      if (editingDay) {
        await updateExerciseDay({
          exerciseId: exercise._id,
          dayId: editingDay._id,
          ...dayFormData,
          description: htmlContent,
        }).unwrap();
        toast.success("Day updated successfully");
      } else {
        await addExerciseDay({
          exerciseId: exercise._id,
          dayData: { ...dayFormData, description: htmlContent },
        }).unwrap();
        toast.success("Day created successfully");
      }
      
      handleCloseDayDialog();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save day");
    } finally {
      setIsSubmittingDay(false);
    }
  };

  const handleDeleteDay = async (day) => {
    if (day.dayNumber === 0) {
      toast.error("Day 0 cannot be deleted");
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${day.title}"?`)) {
      try {
        await removeExerciseDay({
          exerciseId: exercise._id,
          dayId: day._id,
        }).unwrap();
        toast.success("Day deleted successfully");
      } catch (error) {
        toast.error(error?.data?.message || "Failed to delete day");
      }
    }
  };


  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => handleOpenDayDialog()}
        >
          Add Day
        </Button>
      </Box>

      {sortedDays.length > 0 ? (
        <Box>
          {displayedDays.map((day) => (
            <Accordion key={day._id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%">
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    sx={{ mr: 2 }}
                  >
                    {day.title}
                  </Typography>
                  <Box flexGrow={1}>{getPartTypeChips(day)}</Box>
                  <Chip
                    label={
                      day.dayNumber === 0
                        ? "Day 0 (Required)"
                        : `Day ${day.dayNumber}`
                    }
                    color={day.dayNumber === 0 ? "default" : "primary"}
                    size="small"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* Description */}
                  <Card sx={{ mb: 2 }} variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        fontWeight="bold"
                      >
                        Description
                        <Chip
                          label="Required"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 2,
                          minHeight: 60,
                          bgcolor: "#fafafa",
                        }}
                      >
                        <RichTextDisplay content={day.description} />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Other Parts */}
                  {day.dayNumber !== 0 && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {Object.entries(EXERCISE_PART_TYPES).map(([key, config]) => {
                        if (key === "description") return null;

                        const IconComponent = config.icon;
                        const hasPart = day[key];

                        return (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            <Card
                              variant="outlined"
                              sx={{
                                height: "auto",
                                display: "flex",
                                flexDirection: "column",
                                border: hasPart ? 2 : 1,
                                borderColor: hasPart
                                  ? `${config.color}.main`
                                  : "grey.300",
                              }}
                            >
                              <CardContent
                                sx={{
                                  flexGrow: 1,
                                  textAlign: "center",
                                  opacity: hasPart ? 1 : 0.5,
                                }}
                              >
                                <IconComponent
                                  sx={{
                                    fontSize: 36,
                                    color: hasPart
                                      ? `${config.color}.main`
                                      : "grey.400",
                                    mb: 1,
                                  }}
                                />
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  fontWeight="medium"
                                >
                                  {config.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {hasPart ? "Configured" : "Not configured"}
                                </Typography>
                              </CardContent>
                              <CardActions
                                sx={{ justifyContent: "center", pt: 0 }}
                              >
                                <Button
                                  size="small"
                                  color={config.color}
                                  onClick={() =>
                                    handleOpenQuestionManagement(
                                      exercise._id,
                                      day,
                                      key
                                    )
                                  }
                                >
                                  {hasPart ? "Edit" : "Add"}
                                </Button>
                              </CardActions>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  {/* Day Actions */}
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <ExportPdfButton
                      size="small"
                      isAdmin
                      onExport={() => downloadExerciseDayPdf(exercise._id, day.dayNumber, exercise.title)}
                      onExportWithAnswers={() => downloadExerciseDayPdf(exercise._id, day.dayNumber, exercise.title, true)}
                      label="Export PDF"
                    />
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleOpenDayDialog(day)}
                      variant="outlined"
                    >
                      Edit Day
                    </Button>
                    {day.dayNumber !== 0 && (
                      <Button
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteDay(day)}
                        variant="outlined"
                        color="error"
                      >
                        Delete Day
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Loading skeleton for lazy loading */}
          {isLoadingMore && (
            <Box sx={{ mb: 1 }}>
              {[...Array(Math.min(itemsPerLoad, remainingCount))].map((_, index) => (
                <Card key={`skeleton-${index}`} sx={{ mb: 1, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Skeleton variant="text" width="30%" height={30} />
                    <Skeleton variant="rectangular" width={80} height={24} />
                    <Skeleton variant="rectangular" width={80} height={24} />
                  </Box>
                </Card>
              ))}
            </Box>
          )}

          {/* Intersection observer target */}
          {hasMoreDays && (
            <Box
              ref={loaderRef}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
                minHeight: 60,
              }}
            >
              {!isLoadingMore && (
                <Typography variant="body2" color="text.secondary">
                  Scroll to load more days ({remainingCount} remaining)
                </Typography>
              )}
            </Box>
          )}

          {/* Load more button (fallback) */}
          {hasMoreDays && !isLoadingMore && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                onClick={loadMoreDays}
                disabled={isLoadingMore}
              >
                Load More Days ({remainingCount} remaining)
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Alert severity="info">
          No days found. Click "Add Day" to create the first day. Day 0 is
          required for all exercises.
        </Alert>
      )}

      {/* Day Create/Edit Dialog */}
      <Dialog
        open={dayDialogOpen}
        onClose={handleCloseDayDialog}
        maxWidth="md"
        fullWidth
      >
      {isSubmittingDay && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hasImages ? 'Uploading images and saving...' : 'Saving...'}
          </Typography>
        </Box>
      )}
        <DialogTitle>{editingDay ? "Edit Day" : "Create New Day"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Day Title"
              value={dayFormData.title}
              onChange={(e) => handleDayFormChange("title", e.target.value)}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Day Number"
              type="number"
              value={dayFormData.dayNumber}
              onChange={(e) =>
                handleDayFormChange("dayNumber", parseInt(e.target.value) || 0)
              }
              margin="normal"
              inputProps={{ min: 0 }}
              required
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Description <span style={{ color: "red" }}>*</span>
              </Typography>
              <RichTextEditor
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Enter day description..."
                minHeight="200px"
                onContentImgUpload={handleContentImgChange}
                onRichtextStateChange={handleDescriptionStateChange}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDayDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitDay}
            variant="contained"
            disabled={isAddingDay || isUpdatingDay || isSubmittingDay}
          >
            {(isAddingDay || isUpdatingDay || isSubmittingDay) ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            {editingDay ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DayList;
